/**
 * Follows audible output of the remote stream, which transcripts report too late, and its level
 * (0 to 1, in steps of 0.05, about 15 times a second) for a view that moves with the voice.
 */
export function monitorSpeech(
  stream: MediaStream,
  onSpeakingChange: (speaking: boolean) => void,
  onLevel?: (level: number) => void,
): { close(): void } | null {
  if (typeof AudioContext === 'undefined') return null;
  let context: AudioContext | null = null;
  try {
    context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    const muted = context.createGain();
    const samples = new Float32Array(256);
    analyser.fftSize = samples.length;
    analyser.smoothingTimeConstant = 0.15;
    muted.gain.value = 0;
    source.connect(analyser);
    analyser.connect(muted);
    muted.connect(context.destination);
    let frame = 0;
    let speaking = false;
    let silentSince: number | null = null;
    let closed = false;
    let level = 0;
    let levelAt = 0;
    const update = (next: boolean) => {
      if (next === speaking) return;
      speaking = next;
      onSpeakingChange(next);
    };
    const sample = (now: number) => {
      if (closed) return;
      analyser.getFloatTimeDomainData(samples);
      let energy = 0;
      for (const value of samples) energy += value * value;
      const rms = Math.sqrt(energy / samples.length);
      if (onLevel !== undefined && now - levelAt >= 66) {
        const next = Math.round(Math.min(1, rms * 6) * 20) / 20;
        levelAt = now;
        if (next !== level) {
          level = next;
          onLevel(next);
        }
      }
      if (rms >= 0.008) {
        silentSince = null;
        update(true);
      } else if (speaking) {
        silentSince ??= now;
        if (now - silentSince >= 80) update(false);
      }
      frame = requestAnimationFrame(sample);
    };
    frame = requestAnimationFrame(sample);
    void context.resume().catch(() => undefined);
    return {
      close() {
        if (closed) return;
        closed = true;
        cancelAnimationFrame(frame);
        update(false);
        if (level !== 0) onLevel?.(0);
        source.disconnect();
        analyser.disconnect();
        muted.disconnect();
        void context?.close();
      },
    };
  } catch {
    void context?.close();
    return null;
  }
}
